import * as Yup from 'yup';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// File size limit for images (2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;

// Create a validation schema for field slots
const fieldSlotsSchema = DAYS_OF_WEEK.reduce((acc, day) => {
  acc[day] = Yup.array().of(
    Yup.object().shape({
      start_time: Yup.string()
        .required('Start time is required')
        .test('time-format', 'Start time must be a string in HH:MM format', function(value) {
          if (!value) return this.createError({ message: 'Start time is required' });
          return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
        }),
      end_time: Yup.string()
        .required('End time is required')
        .test('time-format', 'End time must be a string in HH:MM format', function(value) {
          if (!value) return this.createError({ message: 'End time is required' });
          return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
        }),
      price: Yup.number()
        .positive('Price must be positive')
        .required('Price is required')
        .nullable()
        .transform((value) => (isNaN(value) || value === '' || value === null ? undefined : Number(value))),
    }).test('slot-time-validation', 'Start time must be less than end time', function(value) {
      if (!value.start_time || !value.end_time) {
        return true; // Let individual field validations handle missing values
      }
      
      const startTime = new Date(`2000-01-01T${value.start_time}:00`);
      const endTime = new Date(`2000-01-01T${value.end_time}:00`);
      
      return startTime < endTime;
    })
  ).min(1, `At least one slot must be defined for ${day}`);
  return acc;
}, {});

// Image validation schema
const imageValidationSchema = Yup.object().shape({
  file: Yup.mixed()
    .test('fileSize', 'File size must be less than 2MB', (value) => {
      if (!value) return true;
      return value.size <= MAX_FILE_SIZE;
    })
    .test('fileType', 'Only JPEG, PNG and GIF files are allowed', (value) => {
      if (!value) return true;
      return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
    }),
  image: Yup.string().optional(), // URL for display
  is_primary: Yup.boolean().default(false),
});

export const groundValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Ground name is required')
    .min(2, 'Ground name must be at least 2 characters')
    .max(100, 'Ground name must not exceed 100 characters'),
  contact_number: Yup.string()
    .matches(/^\+?[\d\s\-\(\)]+$/, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 characters')
    .max(20, 'Phone number must not exceed 20 characters')
    .required('Contact number is required'),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters'),
  opening_time: Yup.string()
    .required('Opening time is required')
    .test('time-format', 'Opening time must be a string in HH:MM format', function(value) {
      if (!value) return this.createError({ message: 'Opening time is required' });
      return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
    }),
  closing_time: Yup.string()
    .required('Closing time is required')
    .test('time-format', 'Closing time must be a string in HH:MM format', function(value) {
      if (!value) return this.createError({ message: 'Closing time is required' });
      return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
    }),
  is_active: Yup.boolean(),
  // sports removed; inferred from ground fields
  amenities: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one amenity must be selected'),
  location: Yup.object().shape({
    city: Yup.string()
      .required('City is required'),
    address: Yup.string()
      .required('Address is required')
      .min(10, 'Address must be at least 10 characters')
      .max(200, 'Address must not exceed 200 characters'),
    latitude: Yup.number()
      .min(-90, 'Latitude must be between -90 and 90')
      .max(90, 'Latitude must be between -90 and 90')
      .nullable()
      .transform((value) => (isNaN(value) || value === '' ? null : Number(value)))
      .optional(),
    longitude: Yup.number()
      .min(-180, 'Longitude must be between -180 and 180')
      .max(180, 'Longitude must be between -180 and 180')
      .nullable()
      .transform((value) => (isNaN(value) || value === '' ? null : Number(value)))
      .optional(),
  }).required('Location information is required'),
  images: Yup.array().of(imageValidationSchema).optional(),
}).test('opening-closing-time', 'Opening time must be less than closing time', function(value) {
  if (!value.opening_time || !value.closing_time) {
    return true; // Let individual field validations handle missing values
  }
  
  const openingTime = new Date(`2000-01-01T${value.opening_time}:00`);
  const closingTime = new Date(`2000-01-01T${value.closing_time}:00`);
  
  return openingTime < closingTime;
}).test('form-validation', 'Please fill in all required fields', function(value) {
  // This test will only run on form submission, not on initial load
  if (this.options.context?.isSubmitting) {
    return true; // Let individual field validations handle the errors
  }
  return true;
});

export const SPORTS_OPTIONS = [
  'Cricket',
  'Football',
  'Hockey',
  'Tennis',
  'Basketball',
  'Volleyball',
  'Badminton',
  'Table Tennis',
  'Swimming',
  'Athletics'
];

// Export constants for reuse
export { MAX_FILE_SIZE, DAYS_OF_WEEK }; 